import { logger } from '@/utils/logger'

export type AuthTokens = {
  token: string
  email: string
  timestamp: number
}

export type JWTPayload = {
  id: string
  exp: number
  iat?: number
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token'
  private readonly EMAIL_KEY = 'auth_email'
  private readonly TIMESTAMP_KEY = 'auth_timestamp'
  private readonly MAX_TOKEN_AGE = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

  /**
   * Decode JWT token (client-side only for expiry checking)
   * Note: Never trust client-side JWT validation for security decisions
   */
  private decodeJWT(token: string): JWTPayload | null {
    try {
      const base64Url = token.split('.')[1]
      if (!base64Url) return null

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      return JSON.parse(jsonPayload)
    } catch (error) {
      logger.error('Failed to decode JWT', error)
      return null
    }
  }

  /**
   * Store JWT token securely in sessionStorage
   */
  setToken(token: string, email: string): void {
    try {
      // Validate token format
      if (!token || !token.includes('.') || token.split('.').length !== 3) {
        throw new Error('Invalid JWT token format')
      }

      // Store token and metadata
      sessionStorage.setItem(this.TOKEN_KEY, token)
      sessionStorage.setItem(this.EMAIL_KEY, email.toLowerCase())
      sessionStorage.setItem(this.TIMESTAMP_KEY, Date.now().toString())
    } catch (error) {
      logger.error('Failed to store token', error)
      throw new Error('Failed to store authentication token')
    }
  }

  /**
   * Get stored JWT token if valid
   */
  getToken(): string | null {
    try {
      const token = sessionStorage.getItem(this.TOKEN_KEY)
      const timestamp = sessionStorage.getItem(this.TIMESTAMP_KEY)

      if (!token || !timestamp) {
        return null
      }

      // Check if token is too old (additional client-side check)
      const tokenAge = Date.now() - parseInt(timestamp)
      if (tokenAge > this.MAX_TOKEN_AGE) {
        this.clearToken()
        return null
      }

      // Check JWT expiry
      const payload = this.decodeJWT(token)
      if (!payload) {
        this.clearToken()
        return null
      }

      // Check if JWT is expired (exp is in seconds, Date.now() is in milliseconds)
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        this.clearToken()
        return null
      }

      return token
    } catch (error) {
      logger.error('Failed to get token', error)
      this.clearToken()
      return null
    }
  }

  /**
   * Get stored authentication data
   */
  getAuthData(): AuthTokens | null {
    const token = this.getToken()
    const email = sessionStorage.getItem(this.EMAIL_KEY)
    const timestamp = sessionStorage.getItem(this.TIMESTAMP_KEY)

    if (!token || !email || !timestamp) {
      return null
    }

    return {
      token,
      email,
      timestamp: parseInt(timestamp),
    }
  }

  /**
   * Clear all authentication data
   */
  clearToken(): void {
    sessionStorage.removeItem(this.TOKEN_KEY)
    sessionStorage.removeItem(this.EMAIL_KEY)
    sessionStorage.removeItem(this.TIMESTAMP_KEY)
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getToken() !== null
  }

  /**
   * Get Authorization header value for API requests
   */
  getAuthHeader(): string | null {
    const token = this.getToken()
    return token ? `Bearer ${token}` : null
  }

  /**
   * Get user ID from JWT token
   */
  getUserId(): string | null {
    const token = this.getToken()
    if (!token) return null

    const payload = this.decodeJWT(token)
    return payload?.id || null
  }

  /**
   * Get token expiry date
   */
  getTokenExpiry(): Date | null {
    const token = this.getToken()
    if (!token) return null

    const payload = this.decodeJWT(token)
    if (!payload?.exp) return null

    return new Date(payload.exp * 1000)
  }

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  isTokenExpiringSoon(): boolean {
    const expiry = this.getTokenExpiry()
    if (!expiry) return true

    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000)
    return expiry < fiveMinutesFromNow
  }

  /**
   * Get time remaining until token expires (in seconds)
   */
  getTimeUntilExpiry(): number {
    const expiry = this.getTokenExpiry()
    if (!expiry) return 0

    const now = new Date()
    const diffInMs = expiry.getTime() - now.getTime()
    return Math.max(0, Math.floor(diffInMs / 1000))
  }
}

// Export singleton instance
export const authService = new AuthService()

// Export utility functions for easier use
export const getAuthToken = () => authService.getToken()
export const getAuthHeader = () => authService.getAuthHeader()
export const isAuthenticated = () => authService.isAuthenticated()
export const clearAuth = () => authService.clearToken()
export const getUserId = () => authService.getUserId()
